import React, { Suspense, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { LayoutChangeEvent } from 'react-native'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import styled from 'styled-components/native'

import { AlertBannerTypes } from '@lyrahealth-inc/shared-app-logic/src/features/alerts/AlertBannerTypes'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { IS_WEB } from '@lyrahealth-inc/ui-core-crossplatform/src/constants'
import { AlertBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/alertBanner/AlertBanner'
import { AppPromoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/appPromoBanner/AppPromoBanner'
import { useTabBarVisibility } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/tabBar/TabBarContext'
import { AuroraAwareBackground } from '@lyrahealth-inc/ui-core-crossplatform/src/templates/auroraAwareBackground/AuroraAwareBackground'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { TabBarSpacer } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { getDetectedCountryFetchStatus, getDetectedCountryISO } from '../../../data/appGlobals/appGlobalsSelectors'
import { getShouldGoFullscreenForMixes } from '../../../data/essentials/essentialsSelector'
import { getId, getIsUserLoggedIn } from '../../../data/user/userSelectors'
import MicrositeAndElsewhereTopBanner from '../../../features/microsite/components/Banners/MicrositeAndElsewhereTopBanner'
import GenericModal from '../../../features/page-components/modals/generic/modal-container'
import { clientSessionContainerId } from '../../constants/appConstants'
import { NEGATIVE_PADDING_PATHS } from '../../constants/routingConstants'
import { useShowAppPromo } from '../../hooks/useShowAppPromo'
import { getMobileBrowserSSOToken } from '../../http/data/mobileBrowserSSO'
import { initializeOneTrust, toggleOneTrustBanner } from '../../utils/cookieUtils'
import { getRouteTitle } from '../../utils/routingUtils'
import { matchPaths } from '../../utils/stringUtils'
import Banner from '../banner/banner-container'
import Header from '../header/Header'
import ToastContainer from '../toastContainer/ToastContainer'

const ClientSessionContainer = styled.View<{
  theme: ThemeType
  shouldAddMargin: boolean
  extraContainerHeight: number
}>(({ theme, shouldAddMargin, extraContainerHeight }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 9,
  ...(shouldAddMargin &&
    IS_WEB && {
      marginTop: extraContainerHeight,
      borderTopWidth: '1px',
      borderTopColor: theme.colors.borderDefault,
    }),
}))

/**
 * Layout for the app which includes the header, banner, models, and footer
 */
export const MainAppLayout: React.FC = ({ children }) => {
  const intl = useIntl()
  const lyraId = useSelector(getId)
  const location = useLocation()
  const isHomePage = ['/'].includes(location.pathname)
  const [containerHeight, setContainerHeight] = useState(0)
  const {
    psaBanner: psaBannerFlag,
    isMobileBrowserToAppLoginEnabled,
    shouldOnlySeeCareNavigatorContactScreenInPlatform,
  } = useFlags()
  const detectedCountryISO = useSelector(getDetectedCountryISO)
  const detectedCountryFetchStatus = useSelector(getDetectedCountryFetchStatus)
  const { checkIsTabBarVisibleWeb, tabBarHeight } = useTabBarVisibility()
  const shouldGoFullscreenForMixes = useSelector(getShouldGoFullscreenForMixes)
  const isLoggedIn = useSelector(getIsUserLoggedIn)

  // Need to add extra margin to compensate for OnboardingNavBar
  const shouldAddMargin = matchPaths(location.pathname, NEGATIVE_PADDING_PATHS)

  useEffect(() => {
    // NOTE: On navigate, location may be updated before window.location
    const nextRouteTitle = getRouteTitle(window.location.origin + location.pathname, intl, '')
    if (nextRouteTitle) {
      document.title = nextRouteTitle
    }
  }, [intl, location])

  useEffect(() => {
    toggleOneTrustBanner(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    if (detectedCountryFetchStatus === 'success' && detectedCountryISO) {
      // Initialize OneTrust cookie banner
      initializeOneTrust(detectedCountryISO, location.pathname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedCountryFetchStatus, detectedCountryISO])

  // Function to fetch mobile SSO token - called when app promo banner button is clicked.
  // Used to log users into the mobile app once they're redirected from the mobile browser web app.
  const fetchMobileSSOToken = async () => {
    if (lyraId && isMobileBrowserToAppLoginEnabled) {
      try {
        return await getMobileBrowserSSOToken(lyraId)
      } catch (error) {
        console.error('MOBILE_BROWSER_SSO: MainAppLayout - Failed to fetch mobile browser SSO token:', error)
        return null
      }
    }
    return null
  }

  const { showAppPromoBanner } = useShowAppPromo()

  return (
    <AuroraAwareBackground>
      {psaBannerFlag?.enabled && (
        <AlertBanner
          show={true}
          contents={psaBannerFlag.message}
          style={psaBannerFlag?.level ?? AlertBannerTypes.INFO}
          includeX={false}
        />
      )}
      <MicrositeAndElsewhereTopBanner />
      <Banner />
      {/* Microsite homepage header behavior is slightly different then rest of web app
      i.e. it is sticky for mobile sized screens so we use StickyView in Homepage.tsx to display header */}
      {/* Users who aren't allowed to use the platform shouldn't be able to see the header */}
      {!isHomePage && !shouldGoFullscreenForMixes && !shouldOnlySeeCareNavigatorContactScreenInPlatform && (
        <>
          {showAppPromoBanner && isMobileBrowserToAppLoginEnabled && isLoggedIn && (
            <AppPromoBanner
              isMobileBrowserToAppLoginEnabled={isMobileBrowserToAppLoginEnabled}
              onFetchMobileSSOToken={fetchMobileSSOToken}
            />
          )}
          {showAppPromoBanner && (!isMobileBrowserToAppLoginEnabled || !isLoggedIn) && <AppPromoBanner />}

          <Header />
        </>
      )}
      <ClientSessionContainer
        shouldAddMargin={shouldAddMargin}
        extraContainerHeight={containerHeight}
        onLayout={(e: LayoutChangeEvent) => {
          setContainerHeight(e.nativeEvent.layout.height)
        }}
      >
        <div id={clientSessionContainerId} />
      </ClientSessionContainer>
      <GenericModal />
      <ToastContainer />
      <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
      {checkIsTabBarVisibleWeb() && <TabBarSpacer tabBarHeight={tabBarHeight} />}
    </AuroraAwareBackground>
  )
}
